import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
// import { useAuth } from '../contexts/AuthContext'; // Not used currently
import './CertificateDisplay.css';
import { TrophyIcon, CheckCircleIcon, EyeIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { getStatusBadge, getTypeText, certificateShortLabel } from '../utils/certificateUtils';


const CERTIFICATES_PER_PAGE = 4;

const CertificateDisplay = ({ gymnastId, showActions = true }) => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [imageUrls, setImageUrls] = useState({});
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCertificates: 0,
    limit: CERTIFICATES_PER_PAGE,
    hasNextPage: false,
    hasPreviousPage: false
  });
  // const { } = useAuth(); // Not needed currently
  
  // Ref to track current image URLs for cleanup
  const imageUrlsRef = useRef({});
  
  // Ref to prevent multiple simultaneous fetches
  const fetchingRef = useRef(false);

  const fetchCertificates = useCallback(async (page = 1) => {
    // Prevent multiple simultaneous fetches
    if (fetchingRef.current) {
      return;
    }
    
    try {
      fetchingRef.current = true;
      setLoading(true);
      const response = await axios.get(`/api/certificates/gymnast/${gymnastId}?page=${page}&limit=${CERTIFICATES_PER_PAGE}`);
      
      // Handle the new response format with pagination
      if (response.data.certificates) {
        setCertificates(response.data.certificates);
        setPagination(response.data.pagination);
        
        // Fetch preview images for each certificate
        const imagePromises = response.data.certificates.map(async (certificate) => {
          try {
            const imageResponse = await axios.get(`/api/certificates/${certificate.id}/preview`, {
              responseType: 'blob'
            });
            const imageUrl = URL.createObjectURL(imageResponse.data);
            return { id: certificate.id, url: imageUrl };
          } catch (err) {
            return { id: certificate.id, url: null, error: err.response?.status || 'unknown' };
          }
        });
        
        const imageResults = await Promise.all(imagePromises);
        const imageUrlMap = {};
        imageResults.forEach(result => {
          imageUrlMap[result.id] = result.url;
        });
        
        // Clean up old image URLs
        Object.values(imageUrlsRef.current).forEach(url => {
          if (url) {
            URL.revokeObjectURL(url);
          }
        });
        
        setImageUrls(imageUrlMap);
        imageUrlsRef.current = imageUrlMap;
      } else {
        // Fallback for old API response format
        setCertificates(response.data);
      }
      
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to fetch certificates');
    } finally {
      setLoading(false);
      fetchingRef.current = false; // Reset fetch lock
    }
  }, [gymnastId]);

  useEffect(() => {
    fetchCertificates(1);
  }, [gymnastId]); // Only depend on gymnastId, not the function itself

  // Cleanup image URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(imageUrlsRef.current).forEach(url => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, []);

  const handlePageChange = (newPage) => {
    fetchCertificates(newPage);
  };

  const handleDownloadCertificate = async (certificateId) => {
    try {
      const response = await axios.get(`/api/certificates/${certificateId}/download`, {
        responseType: 'blob'
      });

      // Create blob URL and download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificate-${certificateId}.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to download certificate. Please try again.');
    }
  };

  const handleViewCertificate = (certificate) => {
    setSelectedCertificate(certificate);
  };

  if (loading) {
    return (
      <div className="certificate-display">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading certificates...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="certificate-display">
        <div className="alert alert-error">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="certificate-display">
      <div className="certificate-header">
        <h3><TrophyIcon style={{ width: '1.2em', height: '1.2em', verticalAlign: 'middle', marginRight: '0.4em', color: 'var(--secondary-color)' }} />Certificates ({pagination.totalCertificates})</h3>
        {pagination.totalCertificates > CERTIFICATES_PER_PAGE && (
          <p className="pagination-info">
            Showing page {pagination.currentPage} of {pagination.totalPages}
          </p>
        )}
      </div>

      {certificates.length === 0 && pagination.totalCertificates === 0 ? (
        <div className="no-certificates">
          <div className="empty-state">
            <TrophyIcon className="empty-icon" style={{ width: '3rem', height: '3rem', color: 'var(--secondary-color)' }} />
            <h4>No Certificates Yet</h4>
            <p>Certificates will appear here when awarded for completed levels and achievements.</p>
          </div>
        </div>
      ) : (
        <>
          <div className="certificates-grid">
            {certificates.map(certificate => (
            <div key={certificate.id} className="certificate-card">
              <div
                className={`certificate-preview${showActions ? ' certificate-preview-clickable' : ''}`}
                onClick={showActions ? () => handleViewCertificate(certificate) : undefined}
                role={showActions ? 'button' : undefined}
                tabIndex={showActions ? 0 : undefined}
                onKeyDown={showActions ? (e) => e.key === 'Enter' && handleViewCertificate(certificate) : undefined}
                aria-label={showActions ? `View certificate for ${certificateShortLabel(certificate)}` : undefined}
              >
                {imageUrls[certificate.id] ? (
                  <img
                    src={imageUrls[certificate.id]}
                    alt={`Certificate for ${certificateShortLabel(certificate)}`}
                    className="certificate-preview-image"
                    loading="lazy"
                  />
                ) : (
                  <div className="certificate-template">
                    <div className="certificate-content">
                      <h4>Certificate of Achievement</h4>
                      <p className="certificate-type">{getTypeText(certificate.type)}</p>
                      <div className="certificate-level">
                        {certificateShortLabel(certificate)}
                      </div>
                      <div className="certificate-level-name">
                        {certificate.level?.name}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="certificate-info">
                <div className="certificate-meta">
                  <div className="certificate-title">
                    {getTypeText(certificate.type)}
                  </div>
                  <div className="certificate-level-badge">
                    <span className="badge badge-primary">
                      {certificateShortLabel(certificate)}
                    </span>
                  </div>
                </div>
                
                <div className="certificate-details">
                  <small className="text-muted">
                    Awarded {new Date(certificate.awardedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </small>
                  {certificate.notes && (
                    <small className="certificate-notes">
                      {certificate.notes}
                    </small>
                  )}
                </div>

                {showActions && (
                  <div className="certificate-actions">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => handleViewCertificate(certificate)}
                    >
                      <EyeIcon style={{ width: '1em', height: '1em', verticalAlign: 'middle', marginRight: '0.3em' }} />View
                    </button>
                    <button
                      className="btn btn-success btn-sm"
                      onClick={() => handleDownloadCertificate(certificate.id)}
                    >
                      <ArrowDownTrayIcon style={{ width: '1em', height: '1em', verticalAlign: 'middle', marginRight: '0.3em' }} />Download
                    </button>
                  </div>
                )}
              </div>
            </div>
                      ))}
          </div>
          
          {/* Pagination Controls */}
          {pagination.totalPages > 1 && (
            <div className="pagination-controls">
              <button 
                className="btn btn-secondary btn-sm"
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.hasPreviousPage}
              >
                Previous
              </button>
              
              <span className="pagination-info">
                Page {pagination.currentPage} of {pagination.totalPages}
              </span>
              
              <button 
                className="btn btn-secondary btn-sm"
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.hasNextPage}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Certificate Modal */}
      {selectedCertificate && (
        <div className="modal-overlay" onClick={() => setSelectedCertificate(null)}>
          <div className="modal certificate-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Certificate Preview</h3>
              <button 
                className="close-button"
                onClick={() => setSelectedCertificate(null)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="certificate-full-preview">
                {imageUrls[selectedCertificate.id] ? (
                  <img 
                    src={imageUrls[selectedCertificate.id]}
                    alt={`Certificate for ${certificateShortLabel(selectedCertificate)}`}
                    className="certificate-modal-image"
                  />
                ) : (
                  <div className="certificate-template large">
                    <div className="certificate-content">
                      <div className="certificate-header-text">Certificate of Achievement</div>
                      <div className="certificate-subtitle">
                        This certifies that
                      </div>
                      <div className="certificate-recipient">
                        [Gymnast Name]
                      </div>
                      <div className="certificate-achievement">
                        {selectedCertificate.level ? 'has successfully completed' : 'has been awarded'}
                      </div>
                      <div className="certificate-level-display">
                        {certificateShortLabel(selectedCertificate)}
                      </div>
                      <div className="certificate-level-name-display">
                        {selectedCertificate.level?.name}
                      </div>
                      <div className="certificate-date">
                        {new Date(selectedCertificate.awardedAt).toLocaleDateString('en-GB', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </div>
                      <div className="certificate-signature">
                        <div className="signature-line"></div>
                        <div className="signature-label">Instructor</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-success"
                onClick={() => handleDownloadCertificate(selectedCertificate.id)}
              >
                <ArrowDownTrayIcon style={{ width: '1em', height: '1em', verticalAlign: 'middle', marginRight: '0.3em' }} />Download Certificate
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setSelectedCertificate(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificateDisplay; 